import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const EMPTY = { title: "", description: "", department: "Sales", assigned_to: "", reporting_manager: "", priority: "normal",
  schedule: "daily", day_of_week: 0, day_of_month: 1, interval_days: 7, start_date: "", due_time: "" };

function scheduleLabel(r) {
  if (r.schedule === "daily") return "Daily";
  if (r.schedule === "weekly") return `Weekly · ${WEEKDAYS[r.day_of_week] || ""}`;
  if (r.schedule === "monthly") return `Monthly · day ${r.day_of_month}`;
  return `Every ${r.interval_days} days from ${r.start_date}`;
}

export default function Recurring() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);

  const load = useCallback(async () => {
    const [r, u, m] = await Promise.all([
      api.get("/office/recurring"), api.get("/office/directory").catch(() => { toast.error("Could not load the user directory — check permissions"); return { data: [] }; }), api.get("/office/meta")]);
    setRows(r.data);
    setUsers(u.data);
    setMeta(m.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const create = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    if (!f.assigned_to) return toast.error("Assignee is required");
    try {
      await api.post("/office/recurring", {
        title: f.title, description: f.description || null, department: f.department,
        assigned_to: f.assigned_to, reporting_manager: f.reporting_manager || null, priority: f.priority,
        schedule: f.schedule, day_of_week: f.schedule === "weekly" ? f.day_of_week : null,
        day_of_month: f.schedule === "monthly" ? parseInt(f.day_of_month) : null,
        interval_days: f.schedule === "custom" ? parseInt(f.interval_days) : null,
        start_date: f.schedule === "custom" ? f.start_date : null,
        due_time: f.due_time || null,
      });
      toast.success("Recurring task created — instances generate automatically");
      setOpen(false);
      setF(EMPTY);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const toggle = async (r) => {
    await api.patch(`/office/recurring/${r.recurring_id}`, { active: !r.active });
    toast.success(r.active ? "Paused" : "Activated");
    load();
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete recurring definition "${r.title}"? Existing task instances stay.`)) return;
    try {
      await api.delete(`/office/recurring/${r.recurring_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="recurring-page">
      <SectionTitle
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setOpen(true)} data-testid="new-recurring-button">
              <Plus className="w-4 h-4 mr-1" /> New Recurring Task</Button>
            <DialogContent className="bg-card border-border max-w-xl" data-testid="recurring-dialog">
              <DialogHeader><DialogTitle className="font-heading text-xl uppercase">New Recurring Task</DialogTitle>
                <DialogDescription className="sr-only">Define an automatically generated recurring office task</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Title *</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="rec-title" placeholder="e.g. Daily sales follow-up" /></div>
                <div><Label className="text-xs">Description</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="rec-description" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Schedule</Label>
                    <Select value={f.schedule} onValueChange={(v) => set("schedule", v)}>
                      <SelectTrigger data-testid="rec-schedule"><SelectValue /></SelectTrigger>
                      <SelectContent>{["daily", "weekly", "monthly", "custom"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select></div>
                  {f.schedule === "weekly" && (
                    <div><Label className="text-xs">Day of week</Label>
                      <Select value={String(f.day_of_week)} onValueChange={(v) => set("day_of_week", parseInt(v))}>
                        <SelectTrigger data-testid="rec-day-of-week"><SelectValue /></SelectTrigger>
                        <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                      </Select></div>)}
                  {f.schedule === "monthly" && (
                    <div><Label className="text-xs">Day of month</Label>
                      <Input type="number" min="1" max="31" value={f.day_of_month} onChange={(e) => set("day_of_month", e.target.value)} data-testid="rec-day-of-month" /></div>)}
                  {f.schedule === "custom" && (
                    <>
                      <div><Label className="text-xs">Every N days</Label>
                        <Input type="number" min="1" value={f.interval_days} onChange={(e) => set("interval_days", e.target.value)} data-testid="rec-interval" /></div>
                      <div><Label className="text-xs">Start date</Label>
                        <Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="rec-start-date" /></div>
                    </>)}
                  <div><Label className="text-xs">Due time</Label>
                    <Input type="time" value={f.due_time} onChange={(e) => set("due_time", e.target.value)} data-testid="rec-due-time" /></div>
                  <div><Label className="text-xs">Department</Label>
                    <Select value={f.department} onValueChange={(v) => set("department", v)}>
                      <SelectTrigger data-testid="rec-department"><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Assigned To *</Label>
                    <Select value={f.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
                      <SelectTrigger data-testid="rec-assignee"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Priority</Label>
                    <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                      <SelectTrigger data-testid="rec-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>{["critical", "high", "normal", "low"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <Button className="w-full bg-primary text-primary-foreground" onClick={create} data-testid="rec-create-submit">Create Recurring Task</Button>
              </div>
            </DialogContent>
          </Dialog>}>
        Recurring Tasks
      </SectionTitle>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="recurring-table">
          <thead><tr className="border-b border-border text-left">
            {["Title", "Schedule", "Assignee", "Dept", "Priority", "Status", ""].map((h) => (
              <th key={h} className="px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>))}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.recurring_id} className="border-b border-border/50" data-testid={`rec-${r.recurring_id}`}>
                <td className="px-4 py-2.5 font-medium">{r.title}<span className="block metric text-[10px] text-muted-foreground">{r.recurring_id}</span></td>
                <td className="px-4 py-2.5 text-xs">{scheduleLabel(r)}{r.due_time ? ` · ${r.due_time}` : ""}</td>
                <td className="px-4 py-2.5 text-xs">{r.assigned_to?.name}</td>
                <td className="px-4 py-2.5 text-xs">{r.department}</td>
                <td className="px-4 py-2.5 text-xs uppercase">{r.priority}</td>
                <td className="px-4 py-2.5">
                  <button onClick={() => toggle(r)} className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${r.active ? "bg-signal-green/15 text-signal-green" : "bg-muted text-muted-foreground"}`}
                    data-testid={`rec-toggle-${r.recurring_id}`}>{r.active ? "active" : "paused"}</button></td>
                <td className="px-4 py-2.5">
                  <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`rec-delete-${r.recurring_id}`}><Trash2 className="w-4 h-4 text-signal-red" /></Button></td>
              </tr>))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No recurring tasks defined</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
